import { useLocation } from 'react-router'
import { track } from '@core/utils/analytics'
import type { FailureAction, FailureCase } from '@product/quies/components/Failures/failures'

// Active Quies session id — merged into every Quies GA4 event as a custom
// parameter so analysts and the /quies-gallery survey can join events back
// to DB rows. Set from _layout.session.tsx when the session loads or changes.
let currentSessionId: string | null = null

export function setQuiesSessionId(id: string | null): void {
    currentSessionId = id
}

/** Where an audio item plays, and which one it is (plan 2026_09_29_almanach_quies.md, A2). `item` is
 *  the host's key for the logical meditation, stable across the player's stream->file swap: it
 *  names the item to the listening guards and never reaches GA4. */
export type AudioContext =
    | {surface: 'session'; item: string; turn: number; sessionId: string | null}
    | {surface: 'history'; item: string}
    | {surface: 'sample'; item: string; page: string}

/** The GA4 params of an audio event: its context without the item key (and the ledger's session). */
export type AudioParams = {surface: AudioContext['surface']; turn?: number; page?: string}

export type AudioSourceKind = 'stream' | 'file'
/** `not_started`: play was pressed and nothing ever sounded — the wait before the first voiced byte
 *  (the 2026-10-03 listener pressed play 20 s before the stream existed), not a stall mid-way. */
export type AudioStopReason = 'stream_ended_early' | 'stalled' | 'suspended' | 'not_started'

export type QuiesEventMap = {
    /** GA4's standard purchase, on the paid return; `transaction_id` is the Checkout Session id. */
    purchase: { transaction_id: string; value: number; currency: string }
    session_begun: { language: string; voice_id?: string }
    voice_preview_played: { voice_id: string }
    composer_response: { action_type: string; turn: number; signal?: 'converge' | 'diverge' }
    audio_play: AudioParams
    /** `hidden`: `document.hidden` at emit; a sleeping tab and a silent one read alike without it. */
    audio_completed: AudioParams & { hidden: boolean }
    audio_listened: AudioParams & { minutes: number; hidden: boolean }
    audio_progress: AudioParams & { percent: number; hidden: boolean }
    audio_paused: AudioParams & { at_seconds: number; hidden: boolean }
    audio_stopped: AudioParams & {
        at_seconds: number
        buffered_seconds: number
        source: AudioSourceKind
        reason: AudioStopReason
        hidden: boolean
    }
    audio_failed: AudioParams & { at_seconds: number; source: AudioSourceKind; code: number; network_state: number }
    audio_swapped: { at_seconds: number; was_playing: boolean; trigger: 'pause' | 'seek' | 'ended' | 'stall' | 'nostart' }
    audio_stream_error_swap: { at_seconds: number; was_playing: boolean }
    session_continued: { turn_count: number }
    /** A landing page's CTA into /session; `page` is the landing path. */
    condition_page_cta: { page: string }
    // One per failure shown (failure handling plan, decision 6): its case and the action pressed.
    failure_shown: { case: FailureCase; action: FailureAction | 'none' }
}

export const quiesTrack = <E extends keyof QuiesEventMap>(
    event: E, params: QuiesEventMap[E]
) => track(event as any, {...(params as object), quies_session_id: currentSessionId} as any)

/** The click handler of a landing page's CTA into /session, naming the page it was pressed on. */
export function useLandingCta(): () => void {
    const {pathname} = useLocation()
    return () => quiesTrack('condition_page_cta', {page: pathname})
}

const DELIVERED_KEY = 'quies:delivered-turns'
const DELIVERED_KEPT = 50
const deliveredInPage = new Set<string>()

/** `deliver`, once per turn id. A reattach (a reload mid-turn, a run another tab holds, a Continue
 *  that finds the meditation still voicing) reads the run's events from the start, so the turn's
 *  `action: deliver` arrives again; the ids already counted are kept in localStorage so that a
 *  reload does not count the turn twice either. */
export function trackDeliverOnce(turnId: string): void {
    if (deliveredInPage.has(turnId)) return
    deliveredInPage.add(turnId)
    let stored: unknown = []
    try {
        stored = JSON.parse(localStorage.getItem(DELIVERED_KEY) ?? '[]')
    } catch {
        // No storage: the in-page set still holds within this page load.
    }
    const counted = Array.isArray(stored) ? stored.filter((id): id is string => typeof id === 'string') : []
    if (counted.includes(turnId)) return
    try {
        localStorage.setItem(DELIVERED_KEY, JSON.stringify([...counted, turnId].slice(-DELIVERED_KEPT)))
    } catch {
        // As above.
    }
    track('deliver', {product: 'quies'})
}
